import React from 'react';
import get from 'lodash/get';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import {Row, Col, Layout, Input, Select, Icon, Alert, Badge, Button} from 'antd';
import { Helmet } from 'react-helmet';

import { getSitesRequest, getSAEsListRequest } from "../../api/requests";

import SAEReportingForm from '../../components/SAEReportingForm/SAEReportingForm';
import classes from './SAEReporting.css';

import isAuthenticated from '../../utils/isAuthenticated';
import { logout } from "../../reducers/user/actions";
import {
  getSAEReadPermission,
  getSAECreatePermission,
  getUserTimezone,
  getUserShortTimezone
} from "../../reducers/user/selectors";
import { withNamespaces } from 'react-i18next';

const { Content } = Layout;
const { Search } = Input;
const { Option } = Select;


class SAEReporting extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      siteID: null,
      sites: [],
      saesBySite: null,
      sae: null,
      subjectID: '',
      isLoading: false,
      errorMessage: '',
      responseMessage: ''
    };
  }

  async componentDidMount() {
    try {
      const { data } = await getSitesRequest();
      this.setState({ sites: data });
    } catch (e) { if (get(e, 'response.status') === 401) { this.props.logout();  return; } console.log(e);}

    if (get(this.props, 'match.params.subjectID')) {
      this.onSearchBySubjectId(get(this.props, 'match.params.subjectID'));
    }

    this.searchSae();
  }


  handleSiteIDChange = async (val) => {
    this.setState({ siteID: val, responseMessage: '', sae: null }, this.searchSae);
  };

  clearSiteSelect = () => {
    this.setState({ siteID: '', saesBySite: null, sae: null }, this.searchSae);
  };

  onSearchBySubjectId = async (value) => {
    this.setState({ subjectID: value, errorMessage: null, sae: null, responseMessage: '', saesBySite: null }, this.searchSae);
  };

  searchSae = async () => {
    this.setState({ isLoading: true });

    try {
      const { data } = await getSAEsListRequest(this.state.subjectID, this.state.siteID);
      this.setState({ saesBySite: data });
    } catch (e) {
      if (get(e, 'response.status') === 401) { this.props.logout();  return; }
      this.setState({ sae: null, errorMessage: get(e, 'response.data.error', 'Error') });
    }

    this.setState({ isLoading: false });
  };

  onSAEDelete = () => {
    this.setState({ sae: null, responseMessage: this.props.i18n.t('NamespaceSAEReporting:Successfully deleted') });
  };

  onSAECancel = () => {
    this.setState({ sae: null, responseMessage: '' });
    this.searchSae();
  };

  handleSearchChange = (e) => {
    this.setState({ subjectID: e.target.value });
  };

  saeToEdit = (sae) => () => {
    this.setState({ sae, saesBySite: null });
  };

  addSae = () => {
    this.setState({sae: {}, saesBySite: null });
  };

  render() {
      const { siteID, sites, saesBySite, sae, subjectID, isLoading, errorMessage, responseMessage } = this.state;
      const { SAEReadPermission, SAECreatePermission } = this.props;

      return (
          <div style={{ backgroundColor: '#FAFAFA' }}>
              <Helmet>
                  <title>{this.props.i18n.t('Trial Management Portal')}: {this.props.i18n.t('SAE Reporting')}</title>
              </Helmet>
              <Layout className={classes.parentContainer}>

                  <Content className={classes.content}>

                    <h3 className='headline-default'>{this.props.i18n.t('NamespaceSAEReporting:SAE reporting')}</h3>

                    {!SAEReadPermission && (<Alert type='error' message={this.props.i18n.t('Permission denied! You cannot perform this action.')} />)}

                    {SAEReadPermission && (<div>
                      <h5 className='headline-default'>{this.props.i18n.t('NamespaceSAEReporting:REPORT A SERIOUS ADVERSE EVENT')}</h5>
                      <div className='ant-alert ant-alert-error'>
                        <p>{this.props.i18n.t('NamespaceSAEReporting:A Serious Adverse Event (SAE) is defined as an event that is')}:</p>
                        <ul>
                          <li>{this.props.i18n.t('NamespaceSAEReporting:Life threatening')}</li>
                          <li>{this.props.i18n.t('NamespaceSAEReporting:Results in persistent or significant disability or incapacity')}</li>
                          <li>{this.props.i18n.t('NamespaceSAEReporting:Results in congenital anomaly/ birth defect')}</li>
                          <li>{this.props.i18n.t('NamespaceSAEReporting:Requires in-patient hospitalization or prolongation of existing hospitalization')}</li>
                          <li>{this.props.i18n.t('NamespaceSAEReporting:Medically important event')}</li>
                        </ul>
                        <p>{this.props.i18n.t('NamespaceSAEReporting:Whether an event is serious or not is based upon common-sense clinical judgement. Serious events include symptomatic ICH, aspiration pneumonia. Examples of non-serious adverse including petechial or asymptomatic ICH, bruising around an IV site, asymptomatic bacteriuria. Non-serious adverse do not require reporting in the DCTMS Trial.')}</p>


                        <p>{this.props.i18n.t('NamespaceSAEReporting:* Adverse events that are not serious do not need to be reported centrally.')}</p>
                        <p>{this.props.i18n.t('NamespaceSAEReporting:** SAEs occurring within 24 hours of randomization should be reported whether expected or not. Expected SAE\'s beyond 24 hours do not need to be reported centrally.')}</p>
                        <p>{this.props.i18n.t('NamespaceSAEReporting:*** If the adverse event was serious, unexpected AND related, it is a SUADR (Serious Unexpected Adverse Drug Reaction), and should be reported irrespective of time of occurrence.')}</p>
                      </div>

                      <br/>

                      {!sae && <Row gutter={12}>
                        <Col md={12}>
                          <b>{this.props.i18n.t('Subject')}</b>
                          <Search
                            enterButton={this.props.i18n.t('Search')}
                            placeholder={this.props.i18n.t('NamespaceSAEReporting:Links to existing subject IDs')}
                            onSearch={this.onSearchBySubjectId}
                            value={subjectID}
                            onChange={this.handleSearchChange}
                          />
                        </Col>
                        <Col md={12}>
                          <b>{this.props.i18n.t('Site ID')}</b>
                          <div className={classes.selectSiteWrapper}>
                            <Select placeholder={this.props.i18n.t('Select Site ID')} onChange={this.handleSiteIDChange} style={{ width: '100%'}} value={siteID} allowClear>
                              {sites.map(site => (
                                <Option key={site.id} value={site.code}>{site.name}</Option>
                              ))}
                              <Option value=''>{this.props.i18n.t('None')}</Option>
                            </Select>

                            {siteID && <span className={classes.selectSiteClear} onClick={this.clearSiteSelect}>{this.props.i18n.t('Clear')}</span>}
                          </div>
                        </Col>
                      </Row>}

                      <br/>

                      {isLoading && <Icon type="loading" />}

                      {!sae && SAECreatePermission && <p><Button type='primary' onClick={this.addSae}>{this.props.i18n.t('NamespaceSAEReporting:Add SAE')}</Button></p>}

                      {saesBySite && saesBySite.length > 0 && (<table className={classes.table}>
                        <thead>
                          <tr>
                            <td>{this.props.i18n.t('Subject ID')}</td>
                            <td>{this.props.i18n.t('NamespaceSAEReporting:Site Number')}</td>
                            <td>{this.props.i18n.t('NamespaceSAEReporting:Sae occurred')}</td>
                            <td>{this.props.i18n.t('Status')}</td>
                            <td>{this.props.i18n.t('Actions')}</td>
                          </tr>
                        </thead>
                        <tbody>
                          {saesBySite.map((sae) => (<tr key={sae.id}>
                            <td>{get(sae, 'subject.code')}</td>
                            <td>
                              {get(sae, 'site.code')}
                            </td>
                            <td>
                              {moment.utc(get(sae, 'sae_date')).tz(this.props.userTimezone || get(sae, 'site.time_zone') || 'UTC').format('YYYY-MM-DD')}
                              &nbsp;{this.props.userShortTimezone || get(sae, 'site.short_tz') || 'UTC'}
                            </td>
                            <td>
                              <Badge count={get(sae, 'state')} style={{ backgroundColor: get(sae, 'state') === 'active' ? '#52c41a' : '#777' }} />
                              &nbsp;&nbsp;
                              <Badge count={get(sae, 'locked') ? this.props.i18n.t('NamespaceSAEReporting:Locked') : this.props.i18n.t('Unlocked')} style={{ backgroundColor: get(sae, 'locked') ? '#777' : '#52c41a' }} />
                            </td>
                            <td>
                              <div className={classes.tableAction}>
                                <Icon type="edit" theme="filled" onClick={this.saeToEdit(sae)} />
                              </div>
                            </td>
                          </tr>))}
                        </tbody>
                      </table>)}

                      {saesBySite && saesBySite.length === 0 && (<p>{this.props.i18n.t('NamespaceSAEReporting:No results found')}</p>)}

                      {errorMessage && (<Alert type='error' message={errorMessage} />)}
                      {responseMessage && (<Alert type='success' message={responseMessage} />)}

                      {sae && <SAEReportingForm {...sae} subjectID={subjectID}  onSubmit={this.onSearchBySubjectId} onDelete={this.onSAEDelete} onCancel={this.onSAECancel} />}
                    </div>)}
                  </Content>

              </Layout>
          </div>
      );
    }
}

const mapStateToProps = (state) => ({
  userTimezone: getUserTimezone(state),
  userShortTimezone: getUserShortTimezone(state),
  SAEReadPermission: getSAEReadPermission(state),
  SAECreatePermission: getSAECreatePermission(state),
});

const mapDispatchToProps = { logout };

export default isAuthenticated(connect(mapStateToProps, mapDispatchToProps)(withNamespaces()(SAEReporting)));



// WEBPACK FOOTER //
// ./src/containers/SAEReporting/SAEReporting.js